import "./styles.css";
import QRCode from "qrcode";
import { ApiClient } from "./backend/client";
import type { Item, SerializedRequest, SerializedResponse, Task } from "./backend/contracts";
import { NativeBrowserBackend } from "./backend/native-backend";
import { probeOutbound } from "./backend/outbound";
import { VaultArtifactStore, type VaultArtifact } from "./backend/vault-artifacts";
import { benchmarkNative, optionalBenchmarkResults, type BenchmarkResult } from "./runtime/benchmark";
import { RUNTIME_CANDIDATES } from "./runtime/candidates";
import { VORTEX_INTEGRATION } from "./integrations/vortex";
import { buildWorkspaceManifest, LOCAL_APP_REGISTRY } from "./integrations/registry";
import { runNetworkDiagnostics, type NetworkDiagnostics } from "./integrations/network-diagnostics";
import { PeerArtifactTransfer } from "./integrations/peer-artifact-transfer";
import { PeerSyncTransfer } from "./integrations/peer-sync-transfer";
import { SameOriginRoomClient, type RoomRelayStatus } from "./integrations/room-relay";
import { exportSyncBundle, importSyncBundle, type SyncBundle } from "./platform/sync-bundle";
import { LocalWorkspaceKernel } from "./platform/kernel";
import { exportWorkspacePackage, restoreWorkspacePackage } from "./platform/workspace-package";
import { exportAppPackage, importAppPackage, IndexedDbAppPackageStore, type InstalledAppPackage } from "./platform/app-package";
import { applyTrustedAppMigration, findAppMigrationPlan } from "./platform/app-migrations";
import { defineLocalApp } from "./platform/sdk";
import { IndexedDbMigrationRecoveryStore, prepareMigrationRecovery, restoreMigrationRecovery, type MigrationRecoveryRecord } from "./platform/migration-recovery";
import { readStorageDiagnostics, requestPersistentStorage, type StorageDiagnostics } from "./backend/storage-diagnostics";
import { readPlatformDiagnostics, type PlatformDiagnostics } from "./integrations/platform-diagnostics";

const backend = new NativeBrowserBackend();
const kernel = new LocalWorkspaceKernel(backend);
for (const app of LOCAL_APP_REGISTRY.map(defineLocalApp)) {
  app.register(kernel);
}
const api = new ApiClient(backend, kernel.openApp("native-workspace"), kernel.openApp("local-tasks"));
const tabChannel = typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel("browser-local-backend-ui");
const root = document.querySelector<HTMLDivElement>("#app");

if (!root) {
  throw new Error("Application root is missing.");
}

root.innerHTML = `
  <main class="shell">
    <header class="hero">
      <div>
        <p class="eyebrow">BROWSER-LOCAL BACKEND</p>
        <h1>A backend that stays in this browser.</h1>
        <p class="lede">Local API routes, durable browser storage, worker execution and runtime diagnostics — with an optional same-origin encrypted room relay.</p>
      </div>
      <div class="truth-card">
        <span class="status-dot" id="runtime-dot"></span>
        <div>
          <strong id="runtime-status">Starting local runtime…</strong>
          <small id="runtime-detail">Local APIs stay in the browser; encrypted room relay is optional.</small>
        </div>
      </div>
    </header>

    <section class="grid two-columns">
      <article class="panel">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">CAPABILITIES</p>
            <h2>What the browser can own</h2>
          </div>
          <button class="secondary" id="refresh-capabilities">Refresh</button>
        </div>
        <div class="capability-list" id="capabilities"></div>
        <div class="result-line" id="kernel-meta">Local kernel contract is starting…</div>
      </article>

      <article class="panel accent-panel">
        <p class="eyebrow">BOUNDARY CHECK</p>
        <h2>What remains outside</h2>
        <ul class="boundary-list">
          <li><span class="pill blocked">NOT AVAILABLE</span> Arbitrary CORS proxy</li>
          <li><span class="pill blocked">NOT AVAILABLE</span> Hidden API secrets</li>
          <li><span class="pill blocked">NOT AVAILABLE</span> Public listener / shared DB</li>
          <li><span class="pill allowed">ALLOWED</span> Direct CORS-aware fetch</li>
        </ul>
        <p class="muted">A Node-compatible runtime inside a tab does not change the browser trust boundary.</p>
      </article>
    </section>

    <section class="panel storage-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">STORAGE REALITY</p>
          <h2>Per-origin persistence</h2>
        </div>
        <button class="secondary" id="request-persistent-storage">Request durable storage</button>
      </div>
      <p class="muted">The browser owns this workspace's storage policy. The app can measure usage and request persistence, but it cannot promise unlimited or permanent storage.</p>
      <div class="network-diagnostics" id="storage-diagnostics"></div>
    </section>

    <section class="panel platform-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">DEVICE COMPATIBILITY</p>
          <h2>How this browser hosts the workspace</h2>
        </div>
        <button class="secondary" id="refresh-platform-diagnostics">Refresh</button>
      </div>
      <p class="muted">This is a capability probe, not a promise that every browser has identical lifecycle or storage behavior.</p>
      <div class="network-diagnostics" id="platform-diagnostics"></div>
      <div class="button-row">
        <button class="secondary" id="install-workspace">Install / Add to Home Screen</button>
      </div>
      <div class="result-line" id="install-result">Install options will be detected by this browser.</div>
    </section>

    <section class="panel vortex-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">PORTABLE ENCRYPTED WORKSPACE</p>
          <h2>Vortex Vault integration</h2>
        </div>
        <a class="primary action-link" id="vortex-link" href="/vortex-vault.html" target="_blank" rel="noopener">Open Vortex Vault</a>
      </div>
      <p class="vortex-copy">A same-origin, offline-cacheable launch surface for the separate Vortex Vault application. Vortex keeps the encrypted <code>.vault</code> file, key material and decrypted records inside its own trusted page.</p>
      <div class="vortex-meta"><span>AGPL-3.0 component</span><span>Opaque .vault boundary</span><span>Direct Share may need network</span></div>
    </section>

    <section class="panel artifact-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">OPAQUE BACKUP LOCKER</p>
          <h2>Keep encrypted .vault artifacts offline</h2>
        </div>
      </div>
      <p class="muted">This shell stores only the encrypted file bytes plus filename/hash metadata. It never opens, decrypts or validates Vortex plaintext. Use Vortex itself to open the downloaded artifact.</p>
      <div class="button-row">
        <label class="file-button secondary">Import .vault backup<input id="vault-artifact-input" type="file" accept=".vault,application/octet-stream" /></label>
      </div>
      <div class="result-line" id="vault-artifact-result">No encrypted artifact stored in this browser.</div>
      <div class="artifact-list" id="vault-artifact-list"></div>
    </section>

    <section class="panel registry-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">OFFLINE APP REGISTRY</p>
          <h2>Portable local workspace</h2>
        </div>
        <div class="button-row">
          <button class="secondary" id="share-workspace">Share workspace</button>
          <button class="secondary" id="export-workspace-manifest">Export manifest</button>
        </div>
      </div>
      <p class="muted">The manifest contains app/capability metadata and opaque artifact metadata only. It never contains vault bytes, keys or decrypted records.</p>
      <div class="registry-list" id="app-registry-list"></div>
      <div class="button-row">
        <button class="secondary" id="export-tasks-app-package">Export Tasks descriptor</button>
        <label class="file-button secondary">Import app descriptor<input id="import-app-package" type="file" accept="application/json,.json" /></label>
      </div>
      <div class="result-line" id="app-package-result">No app descriptor imported yet.</div>
      <div class="registry-list" id="installed-app-packages"></div>
      <button class="secondary" id="download-migration-recovery" type="button" hidden>Download last migration recovery</button>
      <div class="result-line" id="share-result">Workspace link is ready to share.</div>
      <div class="result-line" id="manifest-result">No workspace manifest exported yet.</div>
    </section>

    <section class="panel network-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">NETWORK BOUNDARY</p>
          <h2>Direct Share diagnostics</h2>
        </div>
        <button class="secondary" id="run-network-diagnostics">Run diagnostics</button>
      </div>
      <p class="muted">Local API, vault artifacts and Vortex core do not need Internet. Cross-device Direct Share is a separate WebRTC/network capability; this test only proves browser support and same-device loopback.</p>
      <div class="network-diagnostics" id="network-diagnostics"></div>
    </section>

    <section class="panel relay-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">SAME-ORIGIN ENCRYPTED ROOM</p>
          <h2>Live chat without WebRTC</h2>
        </div>
      </div>
      <p class="muted">The app writes one-shot ciphertext frames to a same-origin TTL mailbox. The hosting CDN carries only the opaque response; room keys and plaintext stay in these browsers. No durable cloud database or WebRTC is required.</p>
      <div class="button-row">
        <button class="primary" id="relay-create-room" type="button">Create room</button>
        <button class="secondary" id="relay-share-invite" type="button" disabled>Share invite</button>
        <button class="danger" id="relay-close-room" type="button">Close room</button>
      </div>
      <label>Invite to share<textarea id="relay-invite" readonly placeholder="Create a room to generate a one-tap invite"></textarea></label>
      <div class="relay-qr-card" id="relay-qr-card" hidden>
        <canvas id="relay-qr" width="256" height="256" aria-label="Scan this QR code to join the encrypted room"></canvas>
        <p class="muted">Scan this QR code with the iPhone Camera app to open the invite.</p>
      </div>
      <label>Invite received<textarea id="relay-invite-input" placeholder="Paste or open an invite URL"></textarea></label>
      <div class="button-row">
        <button class="secondary" id="relay-join-room" type="button">Join room</button>
      </div>
      <div class="result-line" id="relay-status">Room relay is idle.</div>
      <div class="relay-message-row">
        <input id="relay-message-input" maxlength="2000" autocomplete="off" autocapitalize="sentences" enterkeyhint="send" placeholder="Encrypted message" aria-label="Encrypted message" />
        <button class="primary" id="relay-send-message" type="button" disabled>Send</button>
      </div>
      <div class="relay-messages" id="relay-messages" role="log" aria-live="polite" aria-relevant="additions"></div>
    </section>

    <section class="panel peer-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">OPTIONAL PEER TRANSFER</p>
          <h2>Move an opaque .vault backup without a cloud server</h2>
        </div>
      </div>
      <p class="muted">Manual signaling only: copy the offer/answer through a channel you trust. The DataChannel carries encrypted .vault bytes, never Vortex plaintext or keys. Single-artifact transfer can resume a persisted receiver offset; bundles retry safely by hash dedupe. Leave STUN blank for same-LAN/local experiments; cross-network NAT traversal may require a STUN server.</p>
      <label>Artifact to send<select id="peer-artifact-select"></select></label>
      <label>Optional STUN URL<input id="peer-stun-url" placeholder="stun:example.invalid:3478" /></label>
      <div class="peer-grid">
        <div>
          <label>Signal to send<textarea id="peer-signal-out" readonly placeholder="Create an offer or answer"></textarea></label>
          <button class="secondary" id="peer-copy-signal" type="button">Copy signal</button>
        </div>
        <div>
          <label>Signal received<textarea id="peer-signal-in" placeholder="Paste offer or answer here"></textarea></label>
          <div class="button-row">
            <button class="secondary" id="peer-create-offer" type="button">Create offer</button>
            <button class="secondary" id="peer-create-answer" type="button">Create answer</button>
            <button class="primary" id="peer-apply-answer" type="button">Apply answer / send</button>
            <button class="secondary" id="peer-apply-bundle" type="button">Send all artifacts</button>
            <button class="danger" id="peer-reset" type="button">Reset session</button>
          </div>
        </div>
      </div>
      <div class="peer-progress" id="peer-progress" hidden><div class="peer-progress-fill" id="peer-progress-fill"></div></div>
      <div class="result-line" id="peer-transfer-result">No peer transfer session.</div>
    </section>

    <section class="panel sync-panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">OPTIONAL SYNC TRANSPORT</p>
          <h2>Move a validated changeset peer-to-peer</h2>
        </div>
      </div>
      <p class="muted">Manual signaling only. This transports a checksum-validated sync bundle; it never applies changes automatically or creates a shared database.</p>
      <label class="file-button secondary">Load sync bundle<input id="sync-bundle-input" type="file" accept="application/json,.json" /></label>
      <div class="peer-grid">
        <div>
          <label>Sync signal to send<textarea id="sync-signal-out" readonly placeholder="Create a sync offer"></textarea></label>
          <button class="secondary" id="sync-copy-signal" type="button">Copy sync signal</button>
        </div>
        <div>
          <label>Sync signal received<textarea id="sync-signal-in" placeholder="Paste sync offer/answer"></textarea></label>
          <div class="button-row">
            <button class="secondary" id="sync-create-offer" type="button">Create sync offer</button>
            <button class="secondary" id="sync-create-answer" type="button">Create sync answer</button>
            <button class="primary" id="sync-apply-answer" type="button">Send sync bundle</button>
            <button class="danger" id="sync-reset" type="button">Reset sync</button>
          </div>
        </div>
      </div>
      <button class="secondary" id="download-received-sync" type="button" hidden>Download received sync bundle</button>
      <div class="result-line" id="sync-result">No sync bundle loaded.</div>
    </section>

    <section class="panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">LOCAL API</p>
          <h2>CRUD with persistence and optimistic versions</h2>
        </div>
        <button class="secondary" id="call-facade">Call /api/health facade</button>
      </div>
      <form class="item-form" id="item-form">
        <label>Title <input name="title" maxlength="200" required placeholder="A browser-local record" /></label>
        <label>Body <textarea name="body" maxlength="10000" required placeholder="This is persisted in IndexedDB."></textarea></label>
        <button class="primary" type="submit">Create item</button>
      </form>
      <div class="result-line" id="api-result" role="status">Waiting for the local API…</div>
      <div class="items" id="items"></div>
    </section>

    <section class="panel tasks-panel" id="tasks">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">INDEPENDENT LOCAL APP</p>
          <h2>Tasks on the same kernel</h2>
        </div>
        <span class="pill allowed">OFFLINE</span>
      </div>
      <p class="muted">This is a separate versioned resource and IndexedDB store using the same browser-local kernel contract.</p>
      <form class="task-form" id="task-form">
        <label>Task <input name="title" maxlength="200" required placeholder="Ship a local-first feature" /></label>
        <label>Priority <select name="priority"><option value="normal">Normal</option><option value="high">High</option></select></label>
        <button class="primary" type="submit">Add task</button>
      </form>
      <div class="result-line" id="task-result" role="status">No local tasks yet.</div>
      <div class="items" id="tasks-list"></div>
    </section>

    <section class="grid two-columns">
      <article class="panel">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">LIFECYCLE</p>
            <h2>Snapshot and recovery</h2>
          </div>
        </div>
        <div class="button-row">
          <button class="secondary" id="download-snapshot">Download snapshot</button>
          <label class="file-button secondary">Restore snapshot<input id="restore-snapshot" type="file" accept="application/json,.json" /></label>
          <button class="secondary" id="download-workspace-package">Download portable package</button>
          <label class="file-button secondary">Restore portable package<input id="restore-workspace-package" type="file" accept="application/json,.json" /></label>
          <button class="danger" id="restart-runtime">Restart runtime</button>
        </div>
        <div class="result-line" id="lifecycle-result">No lifecycle operation yet.</div>
      </article>

      <article class="panel">
        <div class="panel-heading">
          <div>
            <p class="eyebrow">OUTBOUND POLICY</p>
            <h2>CORS-aware network probe</h2>
          </div>
        </div>
        <label>Target URL <input id="outbound-target" value="https://api.github.com" inputmode="url" /></label>
        <label>Exact allowed origin <input id="outbound-origin" value="https://api.github.com" inputmode="url" /></label>
        <div class="button-row">
          <button class="secondary" id="probe-cors">Probe with CORS</button>
          <button class="secondary" id="probe-no-cors">Probe with no-cors</button>
        </div>
        <div class="result-line" id="outbound-result">No outbound request has been made.</div>
      </article>
    </section>

    <section class="panel">
      <div class="panel-heading">
        <div>
          <p class="eyebrow">RUNTIME BENCHMARK</p>
          <h2>Native baseline and optional Node runtimes</h2>
        </div>
        <button class="primary" id="run-benchmark">Run baseline benchmark</button>
      </div>
      <div class="benchmark-grid" id="benchmark-results"></div>
      <p class="muted">Optional candidates are intentionally not bundled into the baseline app. Their license, browser constraints and measured capabilities must be verified before selection.</p>
    </section>

    <footer>
      <span>Static UI + same-origin ephemeral ciphertext relay</span>
      <span id="diagnostics"></span>
    </footer>
  </main>
`;

const runtimeStatus = element("runtime-status");
const runtimeDetail = element("runtime-detail");
const runtimeDot = element("runtime-dot");
const capabilitiesElement = element("capabilities");
const itemsElement = element("items");
const tasksElement = element("tasks-list");
const apiResult = element("api-result");
const taskResult = element("task-result");
const lifecycleResult = element("lifecycle-result");
const outboundResult = element("outbound-result");
const benchmarkResults = element("benchmark-results");
let vaultArtifactStore: VaultArtifactStore | undefined;
let peerArtifactTransfer: PeerArtifactTransfer | undefined;
let peerSyncTransfer: PeerSyncTransfer | undefined;
let roomRelay: SameOriginRoomClient | undefined;
let autoJoinStarted = false;
let relaySending = false;
let syncBundleBytes: Uint8Array | undefined;
let receivedSyncBundleBytes: Uint8Array | undefined;
let appPackageStore: IndexedDbAppPackageStore | undefined;
let migrationRecoveryStorePromise: Promise<IndexedDbMigrationRecoveryStore> | undefined;
let lastMigrationRecovery: MigrationRecoveryRecord | undefined;
type BeforeInstallPromptEvent = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };
let deferredInstallPrompt: BeforeInstallPromptEvent | undefined;

void boot();
void initVaultArtifacts();
void initAppPackages();
void initPeerSync();
void autoJoinRelayInvite();
window.addEventListener("hashchange", () => void autoJoinRelayInvite());
window.setTimeout(() => void autoJoinRelayInvite(), 0);

async function boot(): Promise<void> {
  try {
    registerServiceWorkerBridge();
    await registerServiceWorker();
    await waitForServiceWorkerControl();
    await backend.start();
    updateCapabilities();
    await renderStorageDiagnostics();
    await renderPlatformDiagnostics();
    await refreshItems();
    await refreshTasks();
    await renderAppRegistry();
    runtimeStatus.textContent = "Native browser backend available";
    runtimeDetail.textContent = "Worker router + IndexedDB + static Service Worker facade.";
    runtimeDot.className = "status-dot online";
  } catch (error) {
    runtimeStatus.textContent = "Local runtime failed";
    runtimeDetail.textContent = error instanceof Error ? error.message : String(error);
    runtimeDot.className = "status-dot offline";
    apiResult.textContent = "The baseline runtime could not start.";
  }
}

element("refresh-capabilities").addEventListener("click", () => updateCapabilities());
element("request-persistent-storage").addEventListener("click", () => void protectStorage());
element("refresh-platform-diagnostics").addEventListener("click", () => void renderPlatformDiagnostics());
element("install-workspace").addEventListener("click", () => void installWorkspace());
element("item-form").addEventListener("submit", (event) => void createItem(event));
element("task-form").addEventListener("submit", (event) => void createTask(event));
element("call-facade").addEventListener("click", () => void callFacade());
element("download-snapshot").addEventListener("click", () => void downloadSnapshot());
element("restore-snapshot").addEventListener("change", (event) => void restoreSnapshot(event));
element("download-workspace-package").addEventListener("click", () => void downloadWorkspacePackage());
element("restore-workspace-package").addEventListener("change", (event) => void restoreWorkspacePackageFile(event));
element("restart-runtime").addEventListener("click", () => void restartRuntime());
element("probe-cors").addEventListener("click", () => void runOutboundProbe("cors"));
element("probe-no-cors").addEventListener("click", () => void runOutboundProbe("no-cors"));
element("run-benchmark").addEventListener("click", () => void runBenchmark());
element<HTMLInputElement>("vault-artifact-input").addEventListener("change", (event) => void importVaultArtifact(event));
element("export-workspace-manifest").addEventListener("click", () => void exportWorkspaceManifest());
element("share-workspace").addEventListener("click", () => void shareWorkspace());
element("export-tasks-app-package").addEventListener("click", () => void exportTasksAppPackage());
element<HTMLInputElement>("import-app-package").addEventListener("change", (event) => void importAppPackageFile(event));
element("download-migration-recovery").addEventListener("click", () => void downloadMigrationRecovery());
element("run-network-diagnostics").addEventListener("click", () => void runDiagnostics());
element("relay-create-room").addEventListener("click", () => void createRelayRoom());
element("relay-share-invite").addEventListener("click", () => void shareRelayInvite());
element("relay-join-room").addEventListener("click", () => void joinRelayRoom());
element("relay-send-message").addEventListener("click", () => void sendRelayMessage());
element<HTMLInputElement>("relay-message-input").addEventListener("keydown", (event) => {
  if (event.key !== "Enter" || event.isComposing) return;
  event.preventDefault();
  void sendRelayMessage();
});
element("relay-close-room").addEventListener("click", () => void closeRelayRoom());
element("peer-create-offer").addEventListener("click", () => void createPeerOffer());
element("peer-create-answer").addEventListener("click", () => void createPeerAnswer());
element("peer-apply-answer").addEventListener("click", () => void applyPeerAnswer());
element("peer-apply-bundle").addEventListener("click", () => void applyPeerBundle());
element("peer-copy-signal").addEventListener("click", () => void copyPeerSignal());
element("peer-reset").addEventListener("click", () => resetPeerTransfer());
element<HTMLInputElement>("sync-bundle-input").addEventListener("change", (event) => void loadSyncBundle(event));
element("sync-create-offer").addEventListener("click", () => void createSyncOffer());
element("sync-create-answer").addEventListener("click", () => void createSyncAnswer());
element("sync-apply-answer").addEventListener("click", () => void sendSyncBundle());
element("sync-copy-signal").addEventListener("click", () => void copySyncSignal());
element("sync-reset").addEventListener("click", () => resetSync());
element("download-received-sync").addEventListener("click", () => downloadReceivedSyncBundle());
window.addEventListener("beforeunload", () => peerArtifactTransfer?.dispose());
window.addEventListener("beforeunload", () => peerSyncTransfer?.dispose());
window.addEventListener("online", () => void renderNetworkStatus({ online: true, webrtcAvailable: false, dataChannelAvailable: false, loopback: "unavailable" }));
window.addEventListener("offline", () => void renderNetworkStatus({ online: false, webrtcAvailable: false, dataChannelAvailable: false, loopback: "unavailable" }));
window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  deferredInstallPrompt = event as BeforeInstallPromptEvent;
  element("install-result").textContent = "Browser install prompt is available.";
});
window.addEventListener("browser-local-backend-changed", () => {
  void refreshItems();
  void refreshTasks();
});
tabChannel?.addEventListener("message", (event) => {
  if (event.data?.type === "items-changed") {
    void refreshItems();
    void refreshTasks();
  }
});
window.addEventListener("storage", (event) => {
  if (event.key === "browser-local-backend-items-changed") {
    void refreshItems();
    void refreshTasks();
  }
});

async function createItem(event: Event): Promise<void> {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const data = new FormData(form);

  try {
    const item = await api.createItem({
      title: String(data.get("title") ?? ""),
      body: String(data.get("body") ?? ""),
    });
    form.reset();
    apiResult.textContent = `Created ${item.id.slice(0, 8)} at version ${item.version}.`;
    notifyOtherTabs();
    await refreshItems();
  } catch (error) {
    apiResult.textContent = errorMessage(error);
  }
}

async function refreshItems(): Promise<void> {
  const items = await api.listItems();
  itemsElement.replaceChildren();

  if (items.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted empty-state";
    empty.textContent = "No items yet. Create one to exercise the local API.";
    itemsElement.append(empty);
    return;
  }

  for (const item of items) {
    itemsElement.append(renderItem(item));
  }
}

async function createTask(event: Event): Promise<void> {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const data = new FormData(form);

  try {
    const task = await api.createTask({ title: String(data.get("title") ?? ""), priority: data.get("priority") === "high" ? "high" : "normal" });
    form.reset();
    taskResult.textContent = `Created task ${task.id.slice(0, 8)} at version ${task.version}.`;
    notifyOtherTabs();
    await refreshTasks();
  } catch (error) {
    taskResult.textContent = errorMessage(error);
  }
}

async function refreshTasks(): Promise<void> {
  const tasks = await api.listTasks();
  tasksElement.replaceChildren();

  if (tasks.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted empty-state";
    empty.textContent = "No tasks yet. Add one to exercise an independent resource.";
    tasksElement.append(empty);
    return;
  }

  for (const task of tasks) {
    tasksElement.append(renderTask(task));
  }
}

function renderTask(task: Task): HTMLElement {
  const article = document.createElement("article");
  article.className = "item-card task-card";

  const content = document.createElement("div");
  const title = document.createElement("h3");
  title.className = task.done ? "task-complete" : "";
  title.textContent = task.title;
  const meta = document.createElement("small");
  meta.textContent = `v${task.version} · ${task.done ? "done" : "open"} · ${task.priority} priority · updated ${new Date(task.updatedAt).toLocaleString()}`;
  content.append(title, meta);

  const actions = document.createElement("div");
  actions.className = "item-actions task-actions";
  const toggle = document.createElement("input");
  toggle.className = "task-check";
  toggle.type = "checkbox";
  toggle.checked = task.done;
  toggle.title = "Toggle task completion";
  toggle.addEventListener("change", async () => {
    toggle.disabled = true;
    try {
      await api.updateTask(task.id, { done: toggle.checked }, task.version);
      taskResult.textContent = `Updated task ${task.id.slice(0, 8)}.`;
      notifyOtherTabs();
      await refreshTasks();
    } catch (error) {
      toggle.checked = task.done;
      taskResult.textContent = errorMessage(error);
      toggle.disabled = false;
    }
  });
  const deleteButton = document.createElement("button");
  deleteButton.className = "danger small-button";
  deleteButton.textContent = "Delete";
  deleteButton.addEventListener("click", async () => {
    try {
      await api.deleteTask(task.id, task.version);
      taskResult.textContent = `Deleted task ${task.id.slice(0, 8)}.`;
      notifyOtherTabs();
      await refreshTasks();
    } catch (error) {
      taskResult.textContent = errorMessage(error);
    }
  });
  actions.append(toggle, deleteButton);
  article.append(content, actions);
  return article;
}

function renderItem(item: Item): HTMLElement {
  const article = document.createElement("article");
  article.className = "item-card";

  const content = document.createElement("div");
  const title = document.createElement("h3");
  title.textContent = item.title;
  const body = document.createElement("p");
  body.textContent = item.body;
  const meta = document.createElement("small");
  meta.textContent = `v${item.version} · updated ${new Date(item.updatedAt).toLocaleString()}`;
  content.append(title, body, meta);

  const actions = document.createElement("div");
  actions.className = "item-actions";
  const deleteButton = document.createElement("button");
  deleteButton.className = "danger small-button";
  deleteButton.textContent = "Delete";
  deleteButton.addEventListener("click", async () => {
    try {
      await api.deleteItem(item.id, item.version);
      apiResult.textContent = `Deleted ${item.id.slice(0, 8)}.`;
      notifyOtherTabs();
      await refreshItems();
    } catch (error) {
      apiResult.textContent = errorMessage(error);
    }
  });
  actions.append(deleteButton);

  article.append(content, actions);
  return article;
}

async function callFacade(): Promise<void> {
  try {
    const response = await fetch("/api/health", { cache: "no-store" });
    const payload = await response.json() as Record<string, unknown>;
    apiResult.textContent = `Same-origin facade: HTTP ${response.status} · ${JSON.stringify(payload)}`;
  } catch (error) {
    apiResult.textContent = `Facade unavailable until the Service Worker is active: ${errorMessage(error)}`;
  }
}

async function downloadSnapshot(): Promise<void> {
  try {
    const snapshot = await backend.snapshot();
    const snapshotBuffer = new ArrayBuffer(snapshot.byteLength);
    new Uint8Array(snapshotBuffer).set(snapshot);
    const blob = new Blob([snapshotBuffer], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `browser-local-backend-${new Date().toISOString().replaceAll(":", "-")}.json`;
    link.click();
    URL.revokeObjectURL(url);
    lifecycleResult.textContent = `Snapshot downloaded (${snapshot.byteLength} bytes).`;
  } catch (error) {
    lifecycleResult.textContent = errorMessage(error);
  }
}

async function restoreSnapshot(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  try {
    await backend.restore(new Uint8Array(await file.arrayBuffer()));
    await refreshItems();
    await refreshTasks();
    lifecycleResult.textContent = "Snapshot restored atomically.";
  } catch (error) {
    lifecycleResult.textContent = errorMessage(error);
  } finally {
    input.value = "";
  }
}

async function downloadWorkspacePackage(): Promise<void> {
  try {
    const bytes = await exportWorkspacePackage(backend, kernel.requireApp("native-workspace"));
    const buffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buffer).set(bytes);
    const blob = new Blob([buffer], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `browser-local-workspace-package-${new Date().toISOString().replaceAll(":", "-")}.json`;
    link.click();
    URL.revokeObjectURL(url);
    lifecycleResult.textContent = `Portable workspace package downloaded (${bytes.byteLength} bytes).`;
  } catch (error) {
    lifecycleResult.textContent = errorMessage(error);
  }
}

async function restoreWorkspacePackageFile(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  try {
    const value = await restoreWorkspacePackage(
      backend,
      new Uint8Array(await file.arrayBuffer()),
      kernel.requireApp("native-workspace"),
    );
    await refreshItems();
    await refreshTasks();
    lifecycleResult.textContent = `Portable package restored for ${value.app.id} data schema v${value.app.dataSchemaVersion}.`;
  } catch (error) {
    lifecycleResult.textContent = errorMessage(error);
  } finally {
    input.value = "";
  }
}

async function restartRuntime(): Promise<void> {
  try {
    lifecycleResult.textContent = "Stopping worker…";
    await backend.stop();
    await backend.start();
    updateCapabilities();
    await refreshItems();
    await refreshTasks();
    lifecycleResult.textContent = "Runtime restarted; committed IndexedDB state is still available.";
  } catch (error) {
    lifecycleResult.textContent = errorMessage(error);
  }
}

async function runOutboundProbe(mode: RequestMode): Promise<void> {
  const target = element<HTMLInputElement>("outbound-target").value.trim();
  const allowedOrigin = element<HTMLInputElement>("outbound-origin").value.trim();
  outboundResult.textContent = `Running ${mode} probe…`;
  const result = await probeOutbound(target, [allowedOrigin], mode);
  outboundResult.textContent = result.ok
    ? `PASS · HTTP ${result.status} · ${result.responseType} · ${result.elapsedMs} ms`
    : `${result.code} · ${result.message} · ${result.elapsedMs} ms`;
}

async function runBenchmark(): Promise<void> {
  benchmarkResults.replaceChildren();
  const native = await benchmarkNative(backend);
  renderBenchmark(native);
  for (const optional of optionalBenchmarkResults()) {
    renderBenchmark(optional);
  }
}

function renderBenchmark(result: BenchmarkResult): void {
  const card = document.createElement("article");
  card.className = "benchmark-card";
  const title = document.createElement("h3");
  title.textContent = result.candidate.label;
  card.append(title);
  for (const marker of result.markers) {
    const row = document.createElement("div");
    row.className = "benchmark-row";
    const status = document.createElement("span");
    status.className = `pill ${marker.status.toLowerCase()}`;
    status.textContent = marker.status;
    const detail = document.createElement("span");
    detail.textContent = `${marker.name}${marker.durationMs === undefined ? "" : ` · ${marker.durationMs} ms`}${marker.detail ? ` · ${marker.detail}` : ""}`;
    row.append(status, detail);
    card.append(row);
  }
  benchmarkResults.append(card);
}

function updateCapabilities(): void {
  const capabilities = backend.capabilities();
  const entries = [
    ["Local API", capabilities.localApi],
    ["Persistence", capabilities.persistence],
    ["Virtual HTTP", capabilities.virtualHttp],
    ["Virtual filesystem", capabilities.virtualFilesystem],
    ["npm runtime", capabilities.npmRuntime],
    ["CORS-aware outbound", capabilities.outboundCorsAwareFetch],
    ["Cross-origin isolated", capabilities.crossOriginIsolated],
  ] as const;
  capabilitiesElement.replaceChildren();
  for (const [label, enabled] of entries) {
    const row = document.createElement("div");
    row.className = "capability-row";
    const status = document.createElement("span");
    status.className = `pill ${enabled ? "allowed" : "blocked"}`;
    status.textContent = enabled ? "AVAILABLE" : "FALLBACK";
    const name = document.createElement("span");
    name.textContent = label;
    row.append(status, name);
    capabilitiesElement.append(row);
  }
  element("diagnostics").textContent = `${capabilities.runtime} · ${RUNTIME_CANDIDATES.length} candidates catalogued`;
  const descriptor = kernel.describe();
  element("kernel-meta").textContent = `Kernel contract v${descriptor.contractVersion} · ${descriptor.apps.length} app manifests · ${descriptor.capabilityStatus.filter((entry) => entry.available).length}/${descriptor.capabilityStatus.length} capabilities available.`;
  const vortexLink = element<HTMLAnchorElement>("vortex-link");
  vortexLink.href = VORTEX_INTEGRATION.path;
  vortexLink.title = `${VORTEX_INTEGRATION.label} · ${VORTEX_INTEGRATION.license}`;
  void renderAppRegistry();
}

async function renderStorageDiagnostics(): Promise<void> {
  renderStorageStatus(await readStorageDiagnostics());
}

async function protectStorage(): Promise<void> {
  const target = element("storage-diagnostics");
  target.textContent = "Requesting browser storage persistence…";
  renderStorageStatus(await requestPersistentStorage());
}

function renderStorageStatus(result: StorageDiagnostics): void {
  const target = element("storage-diagnostics");
  target.replaceChildren();
  const rows: Array<[string, string]> = [
    ["Storage API", result.supported ? "AVAILABLE" : "UNAVAILABLE"],
    ["Persistent mode", result.persisted === null ? "UNKNOWN" : result.persisted ? "GRANTED" : "BEST-EFFORT"],
    ["Usage", result.usageBytes === null ? "UNKNOWN" : formatBytes(result.usageBytes)],
    ["Quota", result.quotaBytes === null ? "UNKNOWN" : formatBytes(result.quotaBytes)],
    ["Estimated remaining", result.remainingBytes === null ? "UNKNOWN" : formatBytes(result.remainingBytes)],
    ["Last request", result.persistenceRequest.toUpperCase()],
  ];
  for (const [label, value] of rows) {
    const row = document.createElement("div");
    row.className = "network-row";
    const name = document.createElement("span");
    name.textContent = label;
    const status = document.createElement("span");
    status.className = `pill ${value === "AVAILABLE" || value === "GRANTED" ? "allowed" : "blocked"}`;
    status.textContent = value;
    row.append(name, status);
    target.append(row);
  }
  if (result.error) {
    const error = document.createElement("small");
    error.className = "network-error";
    error.textContent = result.error;
    target.append(error);
  }
}

async function renderPlatformDiagnostics(): Promise<void> {
  const result = await readPlatformDiagnostics();
  const target = element("platform-diagnostics");
  target.replaceChildren();
  const entries: Array<[string, string]> = [
    ["Platform", result.platform.toUpperCase()],
    ["Home Screen / standalone", result.standalone ? "YES" : "NO"],
    ["Touch points", String(result.touchPoints)],
    ["Service Worker API", result.serviceWorker ? "AVAILABLE" : "UNAVAILABLE"],
    ["IndexedDB", result.indexedDb ? "AVAILABLE" : "UNAVAILABLE"],
    ["Origin Private File System", result.opfs ? "AVAILABLE" : "UNAVAILABLE"],
    ["Storage API", result.storageApi ? "AVAILABLE" : "UNAVAILABLE"],
    ["Persistent storage", result.persistentStorage === null ? "UNKNOWN" : result.persistentStorage ? "GRANTED" : "BEST-EFFORT"],
    ["Web Share API", result.webShare ? "AVAILABLE" : "FALLBACK"],
    ["Clipboard write", result.clipboardWrite ? "AVAILABLE" : "FALLBACK"],
    ["Cross-origin isolated", result.crossOriginIsolated ? "YES" : "NO"],
  ];
  for (const [label, value] of entries) {
    const row = document.createElement("div");
    row.className = "network-row";
    const name = document.createElement("span");
    name.textContent = label;
    const status = document.createElement("span");
    status.className = `pill ${value === "AVAILABLE" || value === "YES" || value === "GRANTED" ? "allowed" : "blocked"}`;
    status.textContent = value;
    row.append(name, status);
    target.append(row);
  }
}

async function shareWorkspace(): Promise<void> {
  const shareData = {
    title: "Browser-local workspace",
    text: "Open my local-first browser workspace.",
    url: window.location.href,
  };
  try {
    if (typeof navigator.share === "function") {
      await navigator.share(shareData);
      element("share-result").textContent = "Workspace share sheet opened.";
      return;
    }
    await copyText(shareData.url);
    element("share-result").textContent = "Workspace link copied; paste it into any browser.";
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      element("share-result").textContent = "Share canceled.";
      return;
    }
    element("share-result").textContent = `Share link ready for manual copy: ${shareData.url}`;
  }
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.append(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Clipboard is unavailable; copy the URL manually.");
}

async function installWorkspace(): Promise<void> {
  try {
    if (deferredInstallPrompt) {
      const prompt = deferredInstallPrompt;
      deferredInstallPrompt = undefined;
      await prompt.prompt();
      const choice = await prompt.userChoice;
      element("install-result").textContent = choice.outcome === "accepted" ? "Workspace install accepted." : "Workspace install dismissed.";
      return;
    }
    const platform = (await readPlatformDiagnostics()).platform;
    if (platform === "ios-like") {
      element("install-result").textContent = "On iPhone/iPad: open Safari Share, then choose Add to Home Screen.";
    } else if (platform === "android-like") {
      element("install-result").textContent = "Open the browser menu and choose Install app or Add to Home screen.";
    } else {
      element("install-result").textContent = "Use your browser menu to install this workspace or add it to the home screen.";
    }
  } catch (error) {
    element("install-result").textContent = errorMessage(error);
  }
}

async function initVaultArtifacts(): Promise<void> {
  try {
    vaultArtifactStore = await VaultArtifactStore.open();
    peerArtifactTransfer = new PeerArtifactTransfer(vaultArtifactStore, (message, error) => {
      element("peer-transfer-result").textContent = message;
      if (error) element("peer-transfer-result").classList.add("error-result");
      void renderVaultArtifacts();
    }, (completed, total) => updatePeerProgress(completed, total));
    await renderVaultArtifacts();
  } catch (error) {
    element("vault-artifact-result").textContent = errorMessage(error);
  }
}

async function initAppPackages(): Promise<void> {
  try {
    appPackageStore = await IndexedDbAppPackageStore.open();
    element("app-package-result").dataset.ready = "true";
    await renderInstalledAppPackages();
  } catch (error) {
    element("app-package-result").dataset.ready = "true";
    element("app-package-result").textContent = `App package registry unavailable: ${errorMessage(error)}`;
  }
}

async function initPeerSync(): Promise<void> {
  peerSyncTransfer = new PeerSyncTransfer((bundle) => {
    void exportSyncBundle(bundle.changes).then((bytes) => {
      receivedSyncBundleBytes = bytes;
      element("download-received-sync").hidden = false;
    });
  }, (message, error) => {
    const target = element("sync-result");
    target.textContent = message;
    target.classList.toggle("error-result", Boolean(error));
  });
}

async function loadSyncBundle(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const bundle = await importSyncBundle(bytes);
    syncBundleBytes = bytes;
    element("sync-result").textContent = `Validated ${bundle.changes.length} sync change(s). Ready to send; receiver must apply explicitly.`;
  } catch (error) {
    syncBundleBytes = undefined;
    element("sync-result").textContent = errorMessage(error);
  } finally {
    input.value = "";
  }
}

async function createSyncOffer(): Promise<void> {
  if (!peerSyncTransfer) return;
  try {
    const signal = await peerSyncTransfer.createOffer(element<HTMLInputElement>("peer-stun-url").value);
    element<HTMLTextAreaElement>("sync-signal-out").value = signal;
    element("sync-result").textContent = "Sync offer created. Copy it to the receiver.";
  } catch (error) {
    element("sync-result").textContent = errorMessage(error);
  }
}

async function createSyncAnswer(): Promise<void> {
  if (!peerSyncTransfer) return;
  try {
    const signal = await peerSyncTransfer.createAnswer(element<HTMLTextAreaElement>("sync-signal-in").value, element<HTMLInputElement>("peer-stun-url").value);
    element<HTMLTextAreaElement>("sync-signal-out").value = signal;
    element("sync-result").textContent = "Sync answer created. Copy it back to the sender.";
  } catch (error) {
    element("sync-result").textContent = errorMessage(error);
  }
}

async function sendSyncBundle(): Promise<void> {
  if (!peerSyncTransfer || !syncBundleBytes) {
    element("sync-result").textContent = "Load a validated sync bundle before sending.";
    return;
  }
  try {
    await peerSyncTransfer.applyAnswerAndSend(element<HTMLTextAreaElement>("sync-signal-in").value, syncBundleBytes);
  } catch (error) {
    element("sync-result").textContent = errorMessage(error);
  }
}

async function copySyncSignal(): Promise<void> {
  const signal = element<HTMLTextAreaElement>("sync-signal-out").value;
  if (!signal) return;
  try {
    await copyText(signal);
    element("sync-result").textContent = "Sync signal copied.";
  } catch (error) {
    element("sync-result").textContent = errorMessage(error);
  }
}

function resetSync(): void {
  peerSyncTransfer?.dispose();
  peerSyncTransfer = undefined;
  syncBundleBytes = undefined;
  receivedSyncBundleBytes = undefined;
  element<HTMLTextAreaElement>("sync-signal-in").value = "";
  element<HTMLTextAreaElement>("sync-signal-out").value = "";
  element("download-received-sync").hidden = true;
  element("sync-result").textContent = "Sync session reset.";
  void initPeerSync();
}

function downloadReceivedSyncBundle(): void {
  if (!receivedSyncBundleBytes) return;
  const buffer = new ArrayBuffer(receivedSyncBundleBytes.byteLength);
  new Uint8Array(buffer).set(receivedSyncBundleBytes);
  const url = URL.createObjectURL(new Blob([buffer], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "received-browser-local-sync-bundle.json";
  link.click();
  URL.revokeObjectURL(url);
}

async function exportTasksAppPackage(): Promise<void> {
  try {
    const bytes = await exportAppPackage(kernel.requireApp("local-tasks"));
    const buffer = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buffer).set(bytes);
    const url = URL.createObjectURL(new Blob([buffer], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "local-tasks.app.json";
    link.click();
    URL.revokeObjectURL(url);
    element("app-package-result").textContent = "Tasks descriptor exported. It contains no executable code and requires a trusted origin entrypoint.";
  } catch (error) {
    element("app-package-result").textContent = errorMessage(error);
  }
}

async function importAppPackageFile(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !appPackageStore) return;
  try {
    const appPackage = await importAppPackage(new Uint8Array(await file.arrayBuffer()));
    const installed = await appPackageStore.install(appPackage);
    element("app-package-result").textContent = `Descriptor validated for ${installed.package.manifest.label}; state: ${installed.status}. No imported code was executed.`;
    await renderInstalledAppPackages();
  } catch (error) {
    element("app-package-result").textContent = errorMessage(error);
  } finally {
    input.value = "";
  }
}

async function renderInstalledAppPackages(): Promise<void> {
  if (!appPackageStore) return;
  const target = element("installed-app-packages");
  target.replaceChildren();
  const packages = await appPackageStore.list();
  for (const installed of packages) {
    target.append(renderInstalledAppPackage(installed));
  }
}

function renderInstalledAppPackage(installed: InstalledAppPackage): HTMLElement {
  const card = document.createElement("article");
  card.className = "registry-card";
  const body = document.createElement("div");
  const title = document.createElement("h3");
  title.textContent = `${installed.package.manifest.label} descriptor`;
  const meta = document.createElement("small");
  const trusted = kernel.getApp(installed.id);
  const migrationPlan = installed.status === "migration-required"
    ? findAppMigrationPlan(installed.id, installed.package.manifest.dataSchemaVersion, trusted?.dataSchemaVersion ?? installed.package.manifest.dataSchemaVersion)
    : undefined;
  const migrationText = migrationPlan ? ` · ${migrationPlan.fromDataSchemaVersion}→${migrationPlan.toDataSchemaVersion}: ${migrationPlan.steps.map((step) => step.label).join(" ")}` : "";
  meta.textContent = `${installed.status.toUpperCase()} · v${installed.package.manifest.version} · schema v${installed.package.manifest.dataSchemaVersion} · trusted entrypoint required${migrationText}`;
  body.append(title, meta);
  card.append(body);
  if (installed.status !== "installed") {
    const trustedMatch = Boolean(
      trusted &&
      trusted.version === installed.package.manifest.version &&
      trusted.path === installed.package.entrypoint.path &&
      trusted.storageNamespace === installed.package.manifest.storageNamespace,
    );
    const action = document.createElement("button");
    action.className = "secondary registry-action";
    action.textContent = !trustedMatch ? "No trusted entrypoint" : installed.status === "migration-required" ? "Migrate + approve" : "Approve trusted origin";
    action.disabled = !trustedMatch || (installed.status === "migration-required" && !migrationPlan);
    if (trustedMatch) {
      action.addEventListener("click", () => void approveInstalledAppPackage(installed.id));
    }
    card.append(action);
  }
  return card;
}

async function approveInstalledAppPackage(id: string): Promise<void> {
  if (!appPackageStore) return;
  try {
    const trusted = kernel.getApp(id);
    const installed = await appPackageStore.get(id);
    if (!trusted || !installed || trusted.version !== installed.package.manifest.version || trusted.path !== installed.package.entrypoint.path) {
      throw new Error("No matching trusted same-origin entrypoint is available.");
    }
    const migrationPlan = installed.status === "migration-required"
      ? findAppMigrationPlan(id, installed.package.manifest.dataSchemaVersion, trusted.dataSchemaVersion)
      : undefined;
    if (installed.status === "migration-required" && !migrationPlan) {
      throw new Error("No trusted migration path is registered for this app schema.");
    }
    if (migrationPlan) {
      const recoveryStore = await getMigrationRecoveryStore();
      const recovery = await prepareMigrationRecovery(backend, id, migrationPlan.fromDataSchemaVersion, migrationPlan.toDataSchemaVersion);
      lastMigrationRecovery = recovery;
      await recoveryStore.put(recovery);
      try {
        const currentPackage = await importAppPackage(await exportAppPackage(trusted));
        await applyTrustedAppMigration(backend, migrationPlan);
        await kernel.regrantApp(id);
        await appPackageStore.migrate(id, currentPackage);
        await recoveryStore.updateStatus(recovery.id, "committed");
        element("download-migration-recovery").hidden = false;
        element("app-package-result").textContent = `Migrated ${installed.package.manifest.label} through ${migrationPlan.steps.length} trusted step(s); permissions regranted and recovery committed.`;
      } catch (error) {
        try {
          await restoreMigrationRecovery(backend, recovery);
          await recoveryStore.updateStatus(recovery.id, "rolled-back");
        } catch (rollbackError) {
          throw new Error(`Migration failed and rollback failed: ${errorMessage(error)}; ${errorMessage(rollbackError)}`);
        }
        throw new Error(`Migration failed and was rolled back: ${errorMessage(error)}`);
      }
    } else {
      await kernel.regrantApp(id);
      await appPackageStore.approve(id);
      element("app-package-result").textContent = `Trusted origin approved for ${installed.package.manifest.label}; permissions regranted.`;
    }
    await renderInstalledAppPackages();
  } catch (error) {
    element("app-package-result").textContent = errorMessage(error);
  }
}

async function getMigrationRecoveryStore(): Promise<IndexedDbMigrationRecoveryStore> {
  migrationRecoveryStorePromise ??= IndexedDbMigrationRecoveryStore.open();
  return migrationRecoveryStorePromise;
}

async function downloadMigrationRecovery(): Promise<void> {
  if (!lastMigrationRecovery) return;
  const buffer = new ArrayBuffer(lastMigrationRecovery.snapshot.byteLength);
  new Uint8Array(buffer).set(lastMigrationRecovery.snapshot);
  const url = URL.createObjectURL(new Blob([buffer], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `migration-recovery-${lastMigrationRecovery.appId}-v${lastMigrationRecovery.fromDataSchemaVersion}-to-v${lastMigrationRecovery.toDataSchemaVersion}.json`;
  link.click();
  URL.revokeObjectURL(url);
  element("app-package-result").textContent = "Migration recovery snapshot downloaded; keep it outside this browser origin.";
}

async function importVaultArtifact(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !vaultArtifactStore) return;
  try {
    const result = await vaultArtifactStore.importFile(file);
    const artifact = result.artifact;
    element("vault-artifact-result").textContent = result.duplicate
      ? `Backup already stored: ${artifact.name} · SHA-256 ${artifact.sha256.slice(0, 16)}…`
      : result.nameConflict
        ? `Stored ${artifact.name}; same filename exists with different bytes.`
        : `Stored opaque backup ${artifact.name} · SHA-256 ${artifact.sha256.slice(0, 16)}…`;
    await renderVaultArtifacts();
    await renderAppRegistry();
  } catch (error) {
    element("vault-artifact-result").textContent = errorMessage(error);
  } finally {
    input.value = "";
  }
}

async function renderVaultArtifacts(): Promise<void> {
  if (!vaultArtifactStore) return;
  const list = element("vault-artifact-list");
  list.replaceChildren();
  const artifacts = await vaultArtifactStore.list();
  renderPeerArtifactOptions(artifacts);
  if (artifacts.length === 0) return;

  for (const artifact of artifacts) {
    const row = document.createElement("article");
    row.className = "artifact-row";
    row.dataset.artifactId = artifact.id;
    const info = document.createElement("div");
    const name = document.createElement("strong");
    name.textContent = artifact.name;
    const meta = document.createElement("small");
    const verification = artifact.lastVerificationOk === undefined
      ? "not verified"
      : artifact.lastVerificationOk
        ? `verified ${formatAge(artifact.lastVerifiedAt)}`
        : "integrity mismatch";
    meta.textContent = `${formatBytes(artifact.size)} · ${formatAge(artifact.importedAt)} · ${verification} · SHA-256 ${artifact.sha256.slice(0, 16)}…`;
    info.append(name, meta);

    const actions = document.createElement("div");
    actions.className = "item-actions";
    const download = document.createElement("button");
    download.className = "secondary small-button";
    download.textContent = "Download";
    download.addEventListener("click", () => void downloadVaultArtifact(artifact));
    const verify = document.createElement("button");
    verify.className = "secondary small-button";
    verify.textContent = "Verify";
    verify.addEventListener("click", () => void verifyVaultArtifact(artifact));
    const remove = document.createElement("button");
    remove.className = "danger small-button";
    remove.textContent = "Remove";
    remove.addEventListener("click", async () => {
      await vaultArtifactStore?.remove(artifact.id);
      await renderVaultArtifacts();
      await renderAppRegistry();
    });
    actions.append(verify, download, remove);
    row.append(info, actions);
    list.append(row);
  }
}

function renderPeerArtifactOptions(artifacts: VaultArtifact[]): void {
  const select = element<HTMLSelectElement>("peer-artifact-select");
  const previous = select.value;
  select.replaceChildren();
  if (artifacts.length === 0) {
    const empty = document.createElement("option");
    empty.textContent = "Import a .vault artifact first";
    empty.value = "";
    select.append(empty);
    return;
  }
  for (const artifact of artifacts) {
    const option = document.createElement("option");
    option.value = artifact.id;
    option.textContent = `${artifact.name} · ${formatBytes(artifact.size)}`;
    select.append(option);
  }
  if (artifacts.some((artifact) => artifact.id === previous)) select.value = previous;
}

async function createPeerOffer(): Promise<void> {
  if (!peerArtifactTransfer) return;
  try {
    const signal = await peerArtifactTransfer.createOffer(element<HTMLInputElement>("peer-stun-url").value);
    element<HTMLTextAreaElement>("peer-signal-out").value = signal;
    element("peer-transfer-result").textContent = "Offer created. Copy it to the receiver.";
  } catch (error) {
    element("peer-transfer-result").textContent = errorMessage(error);
  }
}

async function createPeerAnswer(): Promise<void> {
  if (!peerArtifactTransfer) return;
  try {
    const signal = await peerArtifactTransfer.createAnswer(
      element<HTMLTextAreaElement>("peer-signal-in").value,
      element<HTMLInputElement>("peer-stun-url").value,
    );
    element<HTMLTextAreaElement>("peer-signal-out").value = signal;
    element("peer-transfer-result").textContent = "Answer created. Copy it back to the sender.";
  } catch (error) {
    element("peer-transfer-result").textContent = errorMessage(error);
  }
}

async function applyPeerAnswer(): Promise<void> {
  if (!peerArtifactTransfer) return;
  const artifactId = element<HTMLSelectElement>("peer-artifact-select").value;
  if (!artifactId) {
    element("peer-transfer-result").textContent = "Select an opaque artifact before sending.";
    return;
  }
  try {
    await peerArtifactTransfer.applyAnswer(element<HTMLTextAreaElement>("peer-signal-in").value, artifactId);
    element("peer-transfer-result").textContent = "Encrypted artifact transfer started/completed.";
  } catch (error) {
    element("peer-transfer-result").textContent = errorMessage(error);
  }
}

async function applyPeerBundle(): Promise<void> {
  if (!peerArtifactTransfer || !vaultArtifactStore) return;
  try {
    const artifacts = await vaultArtifactStore.list();
    await peerArtifactTransfer.applyAnswerAndSendBundle(
      element<HTMLTextAreaElement>("peer-signal-in").value,
      artifacts.map((artifact) => artifact.id),
    );
    element("peer-transfer-result").textContent = `Encrypted bundle transfer started/completed for ${artifacts.length} artifacts.`;
  } catch (error) {
    element("peer-transfer-result").textContent = errorMessage(error);
  }
}

async function copyPeerSignal(): Promise<void> {
  const signal = element<HTMLTextAreaElement>("peer-signal-out").value;
  if (!signal) return;
  try {
    await copyText(signal);
    element("peer-transfer-result").textContent = "Signal copied.";
  } catch (error) {
    element("peer-transfer-result").textContent = errorMessage(error);
  }
}

function updatePeerProgress(completed: number, total: number): void {
  const wrapper = element("peer-progress");
  const fill = element("peer-progress-fill");
  if (!total) {
    wrapper.hidden = true;
    fill.style.width = "0%";
    return;
  }
  wrapper.hidden = false;
  fill.style.width = `${Math.min(100, Math.round((completed / total) * 100))}%`;
}

function resetPeerTransfer(): void {
  peerArtifactTransfer?.dispose();
  element<HTMLTextAreaElement>("peer-signal-in").value = "";
  element<HTMLTextAreaElement>("peer-signal-out").value = "";
  element("peer-transfer-result").textContent = "Peer transfer session reset.";
  updatePeerProgress(0, 0);
}

async function downloadVaultArtifact(artifact: VaultArtifact): Promise<void> {
  const value = await vaultArtifactStore?.getBlob(artifact.id);
  if (!value) return;
  const url = URL.createObjectURL(value.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = artifact.name;
  link.click();
  URL.revokeObjectURL(url);
}

async function verifyVaultArtifact(artifact: VaultArtifact): Promise<void> {
  if (!vaultArtifactStore) return;
  try {
    const result = await vaultArtifactStore.verify(artifact.id);
    element("vault-artifact-result").textContent = result.ok
      ? `Integrity verified for ${artifact.name} · SHA-256 matches.`
      : `Integrity mismatch for ${artifact.name}; do not use this backup.`;
    await renderVaultArtifacts();
  } catch (error) {
    element("vault-artifact-result").textContent = errorMessage(error);
  }
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatAge(iso: string | undefined): string {
  if (!iso) return "unknown age";
  const timestamp = Date.parse(iso);
  if (!Number.isFinite(timestamp)) return "unknown age";
  const days = Math.max(0, Math.floor((Date.now() - timestamp) / 86_400_000));
  return days === 0 ? "today" : `${days}d old`;
}

async function runDiagnostics(): Promise<void> {
  const target = element("network-diagnostics");
  target.textContent = "Running local WebRTC loopback diagnostics…";
  renderNetworkStatus(await runNetworkDiagnostics());
}

function renderNetworkStatus(result: NetworkDiagnostics): void {
  const target = element("network-diagnostics");
  target.replaceChildren();
  const entries: Array<[string, string]> = [
    ["Browser network", result.online ? "ONLINE" : "OFFLINE"],
    ["WebRTC API", result.webrtcAvailable ? "AVAILABLE" : "UNAVAILABLE"],
    ["DataChannel", result.dataChannelAvailable ? "AVAILABLE" : "UNAVAILABLE"],
    ["Same-device loopback", result.loopback.toUpperCase()],
    ["ICE gathering", result.iceGatheringState ?? "NOT RUN"],
    ["ICE candidate types", result.candidateTypes?.join(", ") || "NONE"],
    ["Connection state", result.connectionState ?? "NOT RUN"],
  ];
  for (const [label, value] of entries) {
    const row = document.createElement("div");
    row.className = "network-row";
    const name = document.createElement("span");
    name.textContent = label;
    const status = document.createElement("span");
    status.className = `pill ${value === "PASS" || value === "AVAILABLE" || value === "ONLINE" ? "allowed" : "blocked"}`;
    status.textContent = value;
    row.append(name, status);
    target.append(row);
  }
  if (result.error) {
    const error = document.createElement("small");
    error.className = "network-error";
    error.textContent = result.error;
    target.append(error);
  }
}

async function setupRoomRelay(): Promise<SameOriginRoomClient> {
  await roomRelay?.close();
  roomRelay = new SameOriginRoomClient({
    onStatus: (status, detail) => renderRelayStatus(status, detail),
    onMessage: (message) => appendRelayMessage("Peer", message),
  });
  return roomRelay;
}

async function createRelayRoom(): Promise<void> {
  try {
    const relay = await setupRoomRelay();
    const invite = await relay.createRoom();
    element<HTMLTextAreaElement>("relay-invite").value = invite;
    await renderRelayQr(invite);
    element<HTMLButtonElement>("relay-share-invite").disabled = false;
    element<HTMLButtonElement>("relay-send-message").disabled = true;
    renderRelayStatus("waiting-peer", "Invite ready. Share it with the other browser.");
  } catch (error) {
    renderRelayStatus("error", errorMessage(error));
  }
}

async function joinRelayRoom(): Promise<void> {
  try {
    const invite = element<HTMLTextAreaElement>("relay-invite-input").value.trim();
    if (!invite) throw new Error("Paste or open a room invite first.");
    const relay = await setupRoomRelay();
    await relay.joinInvite(invite);
    renderRelayStatus("waiting-peer", "Join request sent. Waiting for encrypted key confirmation.");
  } catch (error) {
    renderRelayStatus("error", errorMessage(error));
  }
}

async function autoJoinRelayInvite(): Promise<void> {
  if (!location.hash.startsWith("#invite=") || autoJoinStarted) return;
  autoJoinStarted = true;
  const input = element<HTMLTextAreaElement>("relay-invite-input");
  input.value = location.href;
  try {
    renderRelayStatus("joining", "Invite detected from QR/link. Joining automatically…");
    const relay = await setupRoomRelay();
    await relay.joinInvite(location.href);
    renderRelayStatus("waiting-peer", "Invite accepted. Waiting for encrypted key confirmation.");
  } catch (error) {
    renderRelayStatus("error", errorMessage(error));
  }
}

async function sendRelayMessage(): Promise<void> {
  try {
    if (relaySending) return;
    if (!roomRelay || roomRelay.currentStatus !== "connected") throw new Error("The encrypted room is not connected.");
    const input = element<HTMLInputElement>("relay-message-input");
    const message = input.value.trim();
    if (!message) return;
    relaySending = true;
    updateRelaySendButton();
    await roomRelay.send(message);
    appendRelayMessage("You", message);
    input.value = "";
  } catch (error) {
    renderRelayStatus("error", errorMessage(error));
  } finally {
    relaySending = false;
    updateRelaySendButton();
  }
}

async function shareRelayInvite(): Promise<void> {
  try {
    const invite = element<HTMLTextAreaElement>("relay-invite").value;
    if (!invite) throw new Error("Create a room first.");
    if (navigator.share) {
      await navigator.share({ title: "Join encrypted room", text: "Open this invite to join the encrypted room.", url: invite });
      renderRelayStatus("waiting-peer", "Invite shared.");
      return;
    }
    await copyText(invite);
    renderRelayStatus("waiting-peer", "Invite copied. Send it to the other browser.");
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      renderRelayStatus("waiting-peer", "Share canceled. The QR and invite are still available.");
      return;
    }
    renderRelayStatus("error", errorMessage(error));
  }
}

async function closeRelayRoom(): Promise<void> {
  await roomRelay?.close();
  roomRelay = undefined;
  element<HTMLTextAreaElement>("relay-invite").value = "";
  element<HTMLDivElement>("relay-qr-card").hidden = true;
  element<HTMLButtonElement>("relay-share-invite").disabled = true;
  relaySending = false;
  updateRelaySendButton();
  renderRelayStatus("closed", "Room closed locally.");
}

async function renderRelayQr(invite: string): Promise<void> {
  const card = element<HTMLDivElement>("relay-qr-card");
  const canvas = element<HTMLCanvasElement>("relay-qr");
  await QRCode.toCanvas(canvas, invite, {
    width: 256,
    margin: 2,
    errorCorrectionLevel: "M",
    color: { dark: "#08111c", light: "#f4f7fb" },
  });
  card.hidden = false;
}

function renderRelayStatus(status: RoomRelayStatus, detail?: string): void {
  const target = element("relay-status");
  const labels: Record<RoomRelayStatus, string> = {
    idle: "IDLE",
    creating: "CREATING",
    joining: "JOINING",
    "waiting-peer": "WAITING FOR PEER",
    connected: "CONNECTED",
    closed: "CLOSED",
    error: "ERROR",
  };
  target.textContent = `${labels[status]}${detail ? ` · ${detail}` : ""}`;
  target.className = `result-line ${status === "connected" ? "success-result" : status === "error" ? "error-result" : ""}`;
  updateRelaySendButton();
}

function updateRelaySendButton(): void {
  element<HTMLButtonElement>("relay-send-message").disabled = relaySending || roomRelay?.currentStatus !== "connected";
}

function appendRelayMessage(sender: string, message: string): void {
  const list = element("relay-messages");
  const row = document.createElement("div");
  row.className = "relay-message";
  row.textContent = `${sender}: ${message}`;
  list.append(row);
  list.scrollTop = list.scrollHeight;
}

async function renderAppRegistry(): Promise<void> {
  const list = element("app-registry-list");
  list.replaceChildren();
  const artifacts = vaultArtifactStore ? await vaultArtifactStore.list() : [];
  const capabilities = backend.capabilities();
  let permissionRecords = [] as Awaited<ReturnType<LocalWorkspaceKernel["listPermissionRecords"]>>;
  try {
    permissionRecords = [...await kernel.listPermissionRecords()];
  } catch {
    // Registry remains useful even if permission metadata storage is unavailable.
  }

  for (const app of kernel.listApps()) {
    const card = document.createElement("article");
    card.className = "registry-card";
    const body = document.createElement("div");
    const title = document.createElement("h3");
    title.textContent = app.label;
    const description = document.createElement("p");
    description.textContent = app.description;
    const meta = document.createElement("small");
    const permission = permissionRecords.some((record) => record.appId === app.id && record.manifestVersion === app.version && record.storageNamespace === app.storageNamespace)
      ? "PERMISSION STORED"
      : "PERMISSION ON FIRST USE";
    meta.textContent = `${kernel.canRun(app) ? "AVAILABLE" : "CAPABILITY MISSING"} · ${permission} · ${app.offline ? "OFFLINE CAPABLE" : "ONLINE"} · ${app.license} · ${app.networkNote}`;
    body.append(title, description, meta);

    const action = document.createElement(app.path.startsWith("/") ? "a" : "button");
    action.className = "secondary registry-action";
    action.textContent = app.path.startsWith("/") ? "Open" : "View";
    if (action instanceof HTMLAnchorElement) {
      action.href = app.path;
      action.target = "_blank";
      action.rel = "noopener";
    } else {
      action.addEventListener("click", () => document.getElementById(app.path.slice(1))?.scrollIntoView({ behavior: "smooth" }));
    }
    card.append(body, action);
    list.append(card);
  }

  element("manifest-result").dataset.artifactCount = String(artifacts.length);
  if (!capabilities.localApi) {
    element("manifest-result").textContent = "Local backend is still starting; registry metadata will refresh automatically.";
  }
}

async function exportWorkspaceManifest(): Promise<void> {
  const artifacts = vaultArtifactStore ? await vaultArtifactStore.list() : [];
  const manifest = buildWorkspaceManifest(backend.capabilities(), artifacts);
  const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `browser-local-workspace-${new Date().toISOString().replaceAll(":", "-")}.json`;
  link.click();
  URL.revokeObjectURL(url);
  element("manifest-result").textContent = `Manifest exported with ${manifest.apps.length} apps and ${manifest.opaqueArtifacts.length} opaque artifacts.`;
}

function registerServiceWorkerBridge(): void {
  const serviceWorker = getServiceWorkerContainer();
  if (!serviceWorker) return;
  serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type !== "browser-local-backend-request" || !event.ports[0]) {
      return;
    }

    void handleServiceWorkerRequest(event.data.request as SerializedRequest, event.ports[0]);
  });
}

async function handleServiceWorkerRequest(request: SerializedRequest, port: MessagePort): Promise<void> {
  try {
    const response = await backend.request(new Request(request.url, {
      method: request.method,
      headers: request.headers,
      body: request.body ?? undefined,
    }));
    const serialized: SerializedResponse = {
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
      body: response.status === 204 ? null : await response.text(),
    };
    port.postMessage({ type: "response", ...serialized });
  } catch (error) {
    port.postMessage({ type: "error", message: errorMessage(error) });
  } finally {
    port.close();
  }
}

async function registerServiceWorker(): Promise<void> {
  const serviceWorker = getServiceWorkerContainer();
  if (!serviceWorker) return;
  await serviceWorker.register("/service-worker.js", { scope: "/" });
}

async function waitForServiceWorkerControl(timeoutMs = 5_000): Promise<void> {
  const serviceWorker = getServiceWorkerContainer();
  if (!serviceWorker) return;
  await serviceWorker.ready;
  if (serviceWorker.controller) return;
  await new Promise<void>((resolve) => {
    const timer = window.setTimeout(() => {
      serviceWorker.removeEventListener("controllerchange", onControllerChange);
      resolve();
    }, timeoutMs);
    const onControllerChange = () => {
      window.clearTimeout(timer);
      serviceWorker.removeEventListener("controllerchange", onControllerChange);
      resolve();
    };
    serviceWorker.addEventListener("controllerchange", onControllerChange, { once: true });
  });
}

function getServiceWorkerContainer(): ServiceWorkerContainer | undefined {
  return (navigator as Navigator & { serviceWorker?: ServiceWorkerContainer }).serviceWorker;
}

function element<T extends HTMLElement = HTMLElement>(id: string): T {
  const target = document.getElementById(id);
  if (!target) {
    throw new Error(`Missing element #${id}`);
  }
  return target as T;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function notifyOtherTabs(): void {
  tabChannel?.postMessage({ type: "items-changed" });
  try {
    localStorage.setItem("browser-local-backend-items-changed", `${Date.now()}-${crypto.randomUUID()}`);
  } catch {
    // Storage may be unavailable in a restrictive browser context; BroadcastChannel remains best effort.
  }
}
